import type { ReactNode } from 'react';
import { skipToken, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useMatch } from 'react-router';

import { REEL_SESSION_QUERY_KEYS, type ReelSession } from '@/entities/reel-session';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { ROUTE_PATTERNS, ROUTES } from '@/shared/config/routes';
import { StaggerItem } from '@/shared/ui/animations';
import {
  PageHeader,
  PageHeaderBreadcrumb,
  PageHeaderBreadcrumbLink,
  PageHeaderBreadcrumbPage,
  PageHeaderIdentity,
} from '@/shared/ui/layout';

/**
 * The reel's title for the header crumb. The frame only watches the session the wizard page
 * fetches (`skipToken`): it requests nothing itself, and stays out of the API client so it can
 * render from the entry chunk before the guard or the lazy page have settled.
 */
const useWizardTitle = (): string => {
  const { t } = useTranslation('reelsCreator');
  const workspaceId = useCurrentWorkspaceId() ?? '';
  const isNew = useMatch(ROUTES.REELS_CREATOR_NEW) !== null;
  const sessionId =
    useMatch(`${ROUTES.REELS_CREATOR}/${ROUTE_PATTERNS.REELS_CREATOR_SESSION}`)?.params.sessionId ??
    '';
  const { data: session } = useQuery<ReelSession>({
    queryKey: REEL_SESSION_QUERY_KEYS.detail(workspaceId, sessionId),
    queryFn: skipToken,
  });

  if (isNew) return t('reelsCreatorWizardPage.newReel');
  return session?.title ?? '';
};

/** The wizard's header, rendered once by the route, so the skeleton and every step swap in under it. */
export const ReelsWizardFrame = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation('reelsCreator');
  const title = useWizardTitle();

  return (
    <div data-testid="reels-creator-wizard" className="page-layout gap-app-inset">
      <StaggerItem index={0} className="shrink-0">
        <PageHeader>
          <PageHeaderIdentity>
            <PageHeaderBreadcrumb>
              <PageHeaderBreadcrumbLink href={ROUTES.REELS_CREATOR}>
                {t('reelsCreatorHomePage.title')}
              </PageHeaderBreadcrumbLink>
              {title ? <PageHeaderBreadcrumbPage>{title}</PageHeaderBreadcrumbPage> : null}
            </PageHeaderBreadcrumb>
          </PageHeaderIdentity>
        </PageHeader>
      </StaggerItem>
      <StaggerItem index={3} className="flex min-h-0 flex-1 flex-col">
        {children}
      </StaggerItem>
    </div>
  );
};
